import React, { useRef, useState, useContext } from 'react';
import { Download, Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserContext } from '@/lib/User';
import axios from 'axios';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';

/**
 * Student ID card — single-sided, CR80 portrait (54 × 85.6 mm) at 420 × 666 px.
 *
 * Three things govern this file:
 *
 * 1. WHAT GOES ON THE CARD. Identity only: institution, photo, name, course,
 *    student number, batch, validity. Phone and email are deliberately NOT on
 *    the card — they are personal data on an object that gets lost, and the
 *    student already knows them. They stay in the Card Details panel beside it.
 * 2. html2canvas 1.4.1 does NOT support flexbox `gap`. Every gap that matters to
 *    the exported PNG is an explicit margin instead, or the download comes out
 *    with elements touching. Margins are safe.
 * 3. Dates are never invented. `enrolled_date` comes from the API and the field
 *    hides itself when missing; there is no expiry column in the schema yet, so
 *    "Valid thru" only renders once the API sends `valid_until`.
 */

const API = 'https://xceededge.com';
const ACCENT = '#FED202';
const CARD_W = 420;
const CARD_H = 666;

/** "2026-01-14 09:30:00" → "Jan 2026". Null for missing or zero dates. */
function monthYear(value) {
  if (!value) return null;
  const iso = String(value).replace(' ', 'T');
  if (iso.startsWith('0000')) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** Same date as "01 / 2026" for the mono validity block. */
function numericMonthYear(value) {
  if (!value) return null;
  const iso = String(value).replace(' ', 'T');
  if (iso.startsWith('0000')) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return `${String(d.getMonth() + 1).padStart(2, '0')} / ${d.getFullYear()}`;
}

export default function StudentIDCard({ course, user, onCourseUpdate }) {
  const cardRef = useRef(null);
  const fileRef = useRef(null);
  const { user: ctxUser } = useContext(UserContext) || {};
  const session = ctxUser?.session || user?.session;

  // Same cache key AccountDetails/CourseDashboard/Profile already use, so a photo
  // uploaded from any of them shows on the card straight away.
  const photoCacheKey = `xceedEdge_photo_${ctxUser?.email || user?.email || user?.id || 'u'}`;
  const [localPhoto, setLocalPhoto] = useState(null);
  const [uploading, setUploading]   = useState(false);

  const studentName = course?.full_name || user?.full_name || user?.name || 'Student Name';
  const studentId   = 'XE-' + (user?.id || course?.id || '2024').toString().slice(-6).toUpperCase().padStart(6, '0');
  const mobile      = course?.phone_number || user?.phone_number || user?.mobile || '—';
  const courseTitle = course?.course_title || course?.title || 'General Learning';
  const email       = course?.email || user?.email || '—';
  const batch       = course?.batch_name || '—';
  let cachedPhoto = null;
  try { cachedPhoto = localStorage.getItem(photoCacheKey); } catch { /* private mode */ }
  const photoUrl = localPhoto || course?.profile_photo || cachedPhoto || null;

  const issuedRaw = course?.enrolled_date || course?.issued_on || null;
  const validRaw  = course?.valid_until || course?.valid_thru || null;
  const issuedLabel = monthYear(issuedRaw);
  const issuedShort = numericMonthYear(issuedRaw);
  const validShort  = numericMonthYear(validRaw);
  const isExpired   = validRaw ? new Date(String(validRaw).replace(' ', 'T')) < new Date() : false;

  const initials = studentName
    .split(' ')
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error('Please choose a JPG, PNG or WebP image'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB'); return;
    }
    if (!session) {
      toast.error('Session expired — please log in again.'); return;
    }

    // Instant local preview so the card updates before the round trip finishes
    const objectUrl = URL.createObjectURL(file);
    setLocalPhoto(objectUrl);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('session', session);
      // Never set Content-Type by hand — axios adds the multipart boundary PHP needs
      const res = await axios.post(`${API}/upload-profile-photo.php`, fd);

      if (res.data?.success) {
        onCourseUpdate?.({ profile_photo: res.data.url });
        try { localStorage.setItem(photoCacheKey, res.data.url); } catch { /* private mode */ }
        setLocalPhoto(res.data.url);
        URL.revokeObjectURL(objectUrl);
        toast.success('Photo added to your ID card');
      } else {
        setLocalPhoto(null);
        URL.revokeObjectURL(objectUrl);
        toast.error(res.data?.message || 'Upload failed');
      }
    } catch (err) {
      setLocalPhoto(null);
      URL.revokeObjectURL(objectUrl);
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;

    // If there's a cross-origin photo, temporarily swap it with a blob URL
    // so html2canvas can read its pixels without CORS blocking the canvas.
    let blobUrl = null;
    let imgEl = null;
    let originalSrc = null;
    if (photoUrl) {
      try {
        const resp = await fetch(photoUrl);
        const blob = await resp.blob();
        blobUrl = URL.createObjectURL(blob);
        imgEl = cardRef.current.querySelector('img[data-profile]');
        if (imgEl) { originalSrc = imgEl.src; imgEl.src = blobUrl; }
        // Give the browser a tick to swap the image
        await new Promise(r => setTimeout(r, 80));
      } catch { /* photo missing — proceed without it */ }
    }

    const canvas = await html2canvas(cardRef.current, { scale: 3, backgroundColor: null });
    const link = document.createElement('a');
    link.download = `${studentName.replace(/\s+/g, '_')}_Student_ID.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

    // Restore original src and free blob memory
    if (imgEl && originalSrc) imgEl.src = originalSrc;
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  };

  // The panel keeps the full record, including the contact details the card
  // itself deliberately leaves off.
  const details = [
    { label: 'Full Name', value: studentName },
    // { label: 'Student ID', value: studentId },
    { label: 'Course', value: courseTitle },
    { label: 'Batch', value: batch },
    { label: 'Mobile', value: mobile },
    { label: 'Email', value: email },
    ...(issuedLabel ? [{ label: 'Issued', value: issuedLabel }] : []),
    ...(validShort ? [{ label: 'Valid thru', value: validShort }] : []),
    { label: 'Status', value: 'PRO Member', highlight: true },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Student ID Card</h2>
        <p className="text-muted-foreground text-sm mt-1">Your official XceedEdge student identification</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* ============== THE ID CARD ============== */}
        {/* Below sm the 420px card is wider than the viewport, so scale the
            wrapper down. html2canvas clones into its own document at scale 3,
            so the export stays high-resolution. */}
        <div className="flex-shrink-0 w-full lg:w-auto origin-top scale-[0.86] -mb-[94px] sm:scale-100 sm:mb-0 flex justify-center lg:block">
          <div
            ref={cardRef}
            style={{ width: CARD_W, height: CARD_H }}
            className="rounded-[20px] overflow-hidden flex flex-col shadow-2xl shadow-black/40"
          >
            <div className="w-full h-full flex flex-col" style={{ background: '#0E0E0E' }}>

              {/* Header */}
              <div className="flex items-center px-6 flex-shrink-0" style={{ background: '#000000', padding: '22px 24px' }}>
                <div
                  className="w-9 h-9 flex-shrink-0 rounded-[10px] flex items-center justify-center"
                  style={{ background: ACCENT }}
                >
                  <CapIcon />
                </div>
                {/* marginLeft, not gap — html2canvas 1.4.1 ignores gap */}
                <div className="flex-1 min-w-0" style={{ marginLeft: 12 }}>
                  <p className="font-heading font-extrabold text-white leading-none" style={{ fontSize: 19, letterSpacing: '-0.015em' }}>
                    XceedEdge
                  </p>
                  <p style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.22em', color: '#7C7C7C', marginTop: 5 }}>
                    INSTITUTE OF TECHNOLOGY
                  </p>
                </div>
                {/* <span
                  className="flex-shrink-0 self-start rounded"
                  style={{ background: ACCENT, color: '#0B0B0B', fontSize: 8, fontWeight: 700, letterSpacing: '0.14em', padding: '4px 8px' }}
                >
                  STUDENT ID
                </span> */}
              </div>

              <div style={{ height: 3, background: ACCENT, flexShrink: 0 }} />

              {/* Body */}
              <div className="flex-1 flex flex-col px-6" style={{ paddingTop: 26 }}>

                {/* Identity */}
                <div className="flex items-start">
                  <div
                    className="flex-shrink-0 rounded-[10px] overflow-hidden relative"
                    style={{ width: 148, height: 176, border: `2px solid ${ACCENT}`, background: '#16160F' }}
                  >
                    {photoUrl && (
                      <img
                        src={photoUrl}
                        alt={studentName}
                        data-profile="1"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}

                    {/* One button fills the slot. With a photo it is a hover
                        overlay; without one it IS the empty state, so the
                        monogram and the prompt can never collide.
                        `data-html2canvas-ignore` keeps it out of the exported
                        PNG — the card downloads as a card, not with UI chrome. */}
                    <button
                      type="button"
                      data-html2canvas-ignore="true"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      aria-label={photoUrl ? 'Change ID photo' : 'Add ID photo'}
                      className={`absolute inset-0 w-full flex flex-col items-center justify-center transition
                        ${photoUrl ? 'opacity-0 hover:opacity-100 focus-visible:opacity-100' : 'opacity-100'}`}
                      style={{ background: photoUrl ? 'rgba(0,0,0,0.62)' : 'transparent' }}
                    >
                      {!photoUrl && (
                        <span
                          className="rounded-full flex items-center justify-center"
                          style={{ width: 64, height: 64, background: 'rgba(254,210,2,0.14)', marginBottom: 14 }}
                        >
                          <span className="font-heading font-bold" style={{ fontSize: 24, color: ACCENT }}>{initials}</span>
                        </span>
                      )}

                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} />
                      ) : (
                        <span
                          className="inline-flex items-center rounded"
                          style={{
                            border: `1px solid ${photoUrl ? 'rgba(255,255,255,0.5)' : 'rgba(254,210,2,0.5)'}`,
                            background: photoUrl ? 'rgba(0,0,0,0.35)' : 'rgba(254,210,2,0.1)',
                            padding: '5px 9px',
                          }}
                        >
                          <Camera className="w-3 h-3" style={{ color: photoUrl ? '#FFFFFF' : ACCENT }} />
                          <span
                            style={{
                              fontSize: 8, fontWeight: 700, letterSpacing: '0.12em',
                              color: photoUrl ? '#FFFFFF' : ACCENT, marginLeft: 5,
                            }}
                          >
                            {photoUrl ? 'CHANGE' : 'ADD PHOTO'}
                          </span>
                        </span>
                      )}
                    </button>
                  </div>

                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={pickPhoto}
                    className="hidden"
                    data-html2canvas-ignore="true"
                  />

                  <div className="flex-1 min-w-0 flex flex-col" style={{ marginLeft: 18, height: 176, overflow: 'hidden' }}>
                    <p
                      className="font-heading font-extrabold text-white"
                      style={{ fontSize: 26, lineHeight: 1.1, letterSpacing: '-0.025em' }}
                    >
                      {studentName}
                    </p>
                  </div>
                </div>

                {/* Identity + contact */}
                <div style={{ marginTop: 26 }}>
                  <DataRow label="ID No." value={studentId} mono />
                  <DataRow label="Course" value={courseTitle} accent />
                  <DataRow label="Phone" value={mobile} mono />
                  <DataRow label="Email" value={email} truncate />
                </div>

                <div className="flex-1" />

                {/* Validity band. items-start with one value size for both: with
                    items-end and mismatched sizes the two labels sat at
                    different heights. Emphasis comes from colour, not size. */}
                {/* {(issuedShort || validShort) && (
                  <div
                    className="flex items-start"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 18, paddingTop: 16 }}
                  >
                    {issuedShort && (
                      <div>
                        <p style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.16em', color: '#7C7C7C' }}>ISSUED</p>
                        <p className="font-mono" style={{ fontSize: 15, fontWeight: 700, color: '#C9C9C9', marginTop: 5, letterSpacing: '0.02em' }}>
                          {issuedShort}
                        </p>
                      </div>
                    )}
                    {validShort && (
                      <div style={{ marginLeft: issuedShort ? 40 : 0 }}>
                        <p style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.16em', color: isExpired ? '#FF9B8F' : '#7C7C7C' }}>
                          VALID THRU
                        </p>
                        <p
                          className="font-mono"
                          style={{ fontSize: 15, fontWeight: 700, color: isExpired ? '#FF6B5A' : ACCENT, marginTop: 5, letterSpacing: '0.02em' }}
                        >
                          {validShort}
                        </p>
                      </div>
                    )}
                  </div>
                )} */}

                {/* Property / return notice — standard on a real ID, and it gives
                    the base of the card something to stand on. */}
                <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12, paddingBottom: 18 }}>
                  <p style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.16em', color: '#7C7C7C' }}>
                    PROPERTY OF XCEEDEDGE · NON-TRANSFERABLE
                  </p>
                  <p style={{ fontSize: 9.5, fontWeight: 500, color: '#9A9A9A', marginTop: 5 }}>
                    If found, return to info@xceededge.com
                  </p>
                </div>
              </div>

              <div style={{ height: 6, background: ACCENT, flexShrink: 0 }} />
            </div>
          </div>
        </div>
        {/* ============== END CARD ============== */}

        {/* Side panel */}
        <div className="space-y-4 flex-1 w-full">
          <div className="glass rounded-2xl border border-white/80 p-5 space-y-4">
            <h3 className="font-heading font-bold text-base">Card Details</h3>
            {details.map(({ label, value, highlight }) => (
              <div
                key={label}
                className="flex justify-between items-center gap-4 border-b border-black/5 pb-3 last:border-0 last:pb-0"
              >
                <span className="text-sm text-muted-foreground font-medium flex-shrink-0">{label}</span>
                <span className={`text-sm font-semibold text-right ${highlight ? 'text-primary' : 'text-foreground'}`}>
                  {value}
                </span>
              </div>
            ))}
          </div>

          <Button
            onClick={downloadCard}
            className="w-full gap-2 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/30 hover:bg-primary/90"
          >
            <Download className="w-4 h-4" />
            Download ID Card (PNG)
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            This card is property of XceedEdge Institute. Report loss to info@xceededge.com.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Pieces ────────────────────────────────────────────────────────────── */

function DataRow({ label, value, mono = false, truncate = false, accent = false }) {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{ padding: '15px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}
    >
      <span
        className="flex-shrink-0"
        style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: '0.14em', color: '#8E8E8E', textTransform: 'uppercase' }}
      >
        {label}
      </span>
      <span
        className={`${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : ''} min-w-0`}
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: accent ? ACCENT : truncate ? '#C9C9C9' : '#F2F2F2',
          letterSpacing: mono ? '0.05em' : 'normal',
          lineHeight: 1.35,
          marginLeft: 16,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function CapIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0B0B0B"
      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 10L12 5 2 10l10 5 10-5z" />
      <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" />
    </svg>
  );
}
